import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Zap, Globe, Shield } from "lucide-react";
import { fetchCategories, fetchTools } from "../lib/api";
import ToolCard from "../components/ToolCard";
import HomeArticle from "../components/HomeArticle";
import Seo from "../components/Seo";
import { getCategoryStyle } from "../lib/categoryStyle";

export default function Home() {
    const [categories, setCategories] = useState([]);
    const [tools, setTools] = useState([]);
    const [query, setQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [c, t] = await Promise.all([fetchCategories(), fetchTools()]);
                setCategories(c);
                setTools(t);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        let list = tools;
        if (activeCategory !== "all") {
            list = list.filter((t) => t.category === activeCategory);
        }
        if (query.trim()) {
            const q = query.toLowerCase();
            list = list.filter(
                (t) =>
                    t.name.toLowerCase().includes(q) ||
                    t.description.toLowerCase().includes(q)
            );
        }
        return list;
    }, [tools, query, activeCategory]);

    return (
        <div className="max-w-7xl mx-auto px-5 md:px-10">
            <Seo
                path="/"
                title="Free AI Writers: AI Tools (GPT-5, Claude, Gemini And More)"
                description="Free AI Writers — 101+ free AI tools, no sign-up, unlimited. Blog writer, story generator, cover letter, image generator, math solver, code generator and more. Powered by GPT-5.2, Gemini 3, Claude Sonnet 4.5."
            />
            {/* HERO */}
            <section className="pt-14 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-7">
                    <div
                        data-testid="hero-brand-strap"
                        className="text-xs md:text-sm font-black uppercase tracking-[0.3em] text-neutral-700 mb-3"
                    >
                        Free AI Writers · Free AI Tools
                    </div>
                    <span
                        data-testid="hero-kicker"
                        className="inline-flex items-center gap-2 bg-[#A7F3D0] nb-border text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full nb-shadow-sm"
                    >
                        <Zap strokeWidth={3} className="w-3.5 h-3.5" />
                        {tools.length || 98}+ tools · free · no sign-up
                    </span>
                    <h1 className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
                        Free AI Writer
                        <br />
                        <span className="bg-[#FDE047] nb-border inline-block px-3 py-1 -rotate-1 mt-2 nb-shadow">
                            Tools
                        </span>
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-neutral-800 max-w-xl">
                        <b>Free AI Writers</b> is a powerful directory of{" "}
                        <b>{tools.length || 101}+ free AI writing tools</b> that help
                        you create blogs, articles, stories, lyrics, cover letters,
                        code, and more in seconds. Our easy-to-use AI tools are
                        designed for fast content creation, SEO writing, and
                        productivity — no sign-up required.
                    </p>

                    <form
                        onSubmit={(e) => e.preventDefault()}
                        className="mt-8 flex gap-3 items-stretch"
                    >
                        <div className="flex-1 relative">
                            <Search
                                strokeWidth={3}
                                className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-black pointer-events-none"
                            />
                            <input
                                data-testid="search-input"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Try 'blog intro' or 'cover letter'…"
                                className="w-full bg-white nb-border-4 rounded-2xl pl-14 pr-5 py-5 text-lg md:text-xl font-bold focus:outline-none focus:nb-shadow-lg nb-shadow transition-all placeholder:text-neutral-400"
                            />
                        </div>
                    </form>

                    <div className="mt-5 flex flex-wrap gap-2 text-sm font-bold">
                        {["AI Blog Writer", "Cover Letter", "Story Generator", "Python Code", "Math Solver"].map(
                            (s) => (
                                <button
                                    key={s}
                                    onClick={() => setQuery(s)}
                                    className="nb-border bg-white px-3 py-1.5 rounded-full hover:bg-[#FDE047] transition-colors"
                                >
                                    {s}
                                </button>
                            )
                        )}
                    </div>
                </div>

                <div className="md:col-span-5 relative">
                    <div
                        className="relative nb-border-4 nb-shadow-lg rounded-3xl bg-[#D8B4FE] p-6 md:p-8 rotate-2 overflow-hidden"
                        style={{
                            backgroundImage:
                                "url(https://static.prod-images.emergentagent.com/jobs/aa971ddd-0b8c-4458-be13-10e03eaf9961/images/152a5d4fda117e0acb7efaf9e4458e6b93462c92a141cb598d4991be8ec31ea5.png)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    >
                        <div className="bg-white nb-border rounded-2xl p-5 -rotate-2">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">
                                /tools/ai-blog-writer
                            </div>
                            <div className="mt-2 font-mono text-sm leading-relaxed">
                                <span className="text-neutral-400">{">"}</span> topic: the rise of pastel neo-brutalism
                                <br />
                                <span className="text-neutral-400">{">"}</span> generating...
                                <br />
                                <span className="bg-[#A7F3D0] px-1">H1: Why Flat Design Is Sooo 2019</span>
                                <br />
                                <span className="nb-cursor">H2: The Chunky Border Rebellion</span>
                            </div>
                        </div>
                        <div className="mt-4 flex gap-2">
                            <span className="bg-[#FDE047] nb-border text-xs font-bold px-3 py-1 rounded-full">
                                GPT-5.2
                            </span>
                            <span className="bg-[#A7F3D0] nb-border text-xs font-bold px-3 py-1 rounded-full">
                                Gemini 3
                            </span>
                            <span className="bg-white nb-border text-xs font-bold px-3 py-1 rounded-full">
                                Claude 4.5
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURE STRIP */}
            <section className="grid md:grid-cols-3 gap-5 md:gap-8">
                {[
                    { icon: Zap, title: "Unlimited & free", desc: "No sign-up, no paywalls, no tricks." },
                    { icon: Globe, title: "Pick your model", desc: "GPT-5.2, Gemini 3, Claude Sonnet 4.5." },
                    { icon: Shield, title: "Privacy first", desc: "Your inputs are not used to train models." },
                ].map((f) => (
                    <div
                        key={f.title}
                        className="nb-border rounded-2xl bg-white p-5 nb-shadow-sm flex items-start gap-4"
                    >
                        <div className="w-11 h-11 rounded-xl nb-border bg-[#A7F3D0] flex items-center justify-center shrink-0">
                            <f.icon strokeWidth={3} className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="font-display font-black text-xl">{f.title}</div>
                            <div className="text-sm text-neutral-700 mt-1">{f.desc}</div>
                        </div>
                    </div>
                ))}
            </section>

            {/* BIO / SEO NARRATION */}
            <section
                id="about"
                data-testid="bio-section"
                className="mt-16 md:mt-24 nb-border-4 rounded-3xl bg-[#FDE047] nb-shadow-lg p-7 md:p-12"
            >
                <div className="text-xs font-bold uppercase tracking-widest mb-3">
                    Welcome to Free AI Writers
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter leading-tight">
                    No Sign-up. Unlimited. Free forever.
                </h2>
                <div className="mt-5 text-base md:text-lg text-black/85 leading-relaxed max-w-4xl space-y-4">
                    <p>
                        <b>Free AI Writers</b> is your one-stop playground for{" "}
                        <b>101+ free AI tools</b> — built for students, creators,
                        marketers, and coders who want powerful AI without the monthly
                        bill or the sign-up form.
                    </p>
                    <p>
                        Whether you need <b>free AI blog writing</b>,{" "}
                        <b>AI story writing</b>, an <b>AI cover letter generator free</b>,{" "}
                        help to <b>rewrite a paragraph with AI free</b>, an{" "}
                        <b>AI essay writer free no login</b>, a{" "}
                        <b>math equation solver with steps</b>, a{" "}
                        <b>free AI image generator no watermark</b>, an{" "}
                        <b>AI code generator Python free</b>, an{" "}
                        <b>AI rap lyrics generator free</b>, an{" "}
                        <b>AI email response generator free</b>, an{" "}
                        <b>AI TikTok caption generator</b>, or an{" "}
                        <b>AI pick-up line generator funny</b> — we have a dedicated tool
                        for it.
                    </p>
                    <p>
                        Generate output <b>instantly</b> with our free AI tools.
                        Powered by <b>GPT-5.2</b>, <b>Gemini 3</b>, <b>Claude Sonnet 4.5</b>,
                        and a <b>ChatGPT alternative</b> — no login required, no
                        watermark, no credit card.
                    </p>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                    {[
                        { label: "Free AI Blog Writing", slug: "ai-blog-writer" },
                        { label: "AI Story Generator", slug: "story-generator" },
                        { label: "AI Cover Letter Free", slug: "cover-letter-generator" },
                        { label: "Rewrite Paragraph AI", slug: "paragraph-rewriter" },
                        { label: "Essay Writer No Login", slug: "ai-essay-writer" },
                        { label: "Math Solver with Steps", slug: "math-equation-solver" },
                        { label: "AI Image Generator", slug: "ai-image-generator" },
                        { label: "Python Code Generator", slug: "python-code-generator" },
                        { label: "AI Rap Lyrics", slug: "rap-lyrics-generator" },
                        { label: "Email Response AI", slug: "email-response-generator" },
                        { label: "TikTok Captions", slug: "tiktok-comment-generator" },
                        { label: "Funny Pick-Up Lines", slug: "pick-up-line-generator" },
                        { label: "ChatGPT Alternative", slug: "chatgpt-alternative" },
                    ].map((k) => (
                        <Link
                            key={k.slug}
                            to={`/tool/${k.slug}`}
                            data-testid={`bio-pill-${k.slug}`}
                            className="nb-border bg-white text-black text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#A7F3D0] hover:-translate-y-[2px] hover:shadow-[2px_2px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all"
                        >
                            {k.label}
                        </Link>
                    ))}
                </div>
            </section>

            {/* CATEGORIES */}
            <section id="categories" className="mt-24 md:mt-32">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                            01 — browse
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
                            Pick a category, pick a tool.
                        </h2>
                    </div>
                    <div className="text-sm font-semibold text-neutral-600">
                        {categories.reduce((s, c) => s + (c.tool_count || 0), 0)} tools,
                        growing.
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {categories.map((c) => {
                        const style = getCategoryStyle(c.color);
                        return (
                            <Link
                                key={c.slug}
                                to={`/category/${c.slug}`}
                                data-testid={`category-card-${c.slug}`}
                                className={`relative ${style.bg} nb-border rounded-2xl p-6 nb-shadow hover:-translate-y-1 hover:shadow-[8px_8px_0px_#0A0A0A] transition-all flex flex-col gap-3 min-h-[180px]`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-widest font-bold">
                                        {c.tool_count} tools
                                    </span>
                                    <ArrowRight strokeWidth={2.5} className="w-5 h-5" />
                                </div>
                                <div className="font-display text-2xl md:text-3xl font-black leading-tight mt-auto">
                                    {c.name}
                                </div>
                                <div className="text-sm font-medium text-black/80">
                                    {c.tagline}
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* TOOLS GRID */}
            <section id="tools" className="mt-24 md:mt-32">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                            02 — generate
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
                            All tools, one page.
                        </h2>
                    </div>
                </div>

                {/* Category filter pills */}
                <div className="mt-6 flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                    <button
                        data-testid="category-filter-all"
                        onClick={() => setActiveCategory("all")}
                        className={`shrink-0 px-4 py-2 rounded-full font-bold text-sm nb-border whitespace-nowrap transition-all ${activeCategory === "all" ? "bg-black text-white nb-shadow-sm" : "bg-white"}`}
                    >
                        All ({tools.length})
                    </button>
                    {categories.map((c) => {
                        const style = getCategoryStyle(c.color);
                        const active = activeCategory === c.slug;
                        return (
                            <button
                                key={c.slug}
                                data-testid={`category-filter-${c.slug}`}
                                onClick={() => setActiveCategory(c.slug)}
                                className={`shrink-0 px-4 py-2 rounded-full font-bold text-sm nb-border whitespace-nowrap transition-all ${active ? `${style.bg} nb-shadow-sm` : "bg-white"}`}
                            >
                                {c.name}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div
                                key={i}
                                className="nb-border rounded-2xl bg-white p-5 h-48 animate-pulse"
                            />
                        ))}
                    </div>
                ) : (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                        {filtered.map((t) => (
                            <ToolCard key={t.slug} tool={t} />
                        ))}
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="mt-10 nb-border rounded-2xl bg-white p-10 text-center">
                        <div className="font-display text-2xl font-black">Nothing here.</div>
                        <div className="text-sm text-neutral-700 mt-1">
                            Try a different search or category.
                        </div>
                    </div>
                )}
            </section>

            {/* SEO LONG-READ ARTICLE — placed at the end of homepage */}
            <HomeArticle />
        </div>
    );
}
